/**
 * Google — Business Profile, Ads, Reviews and SEO in the Hub.
 *
 * The Overview tab is deliberately a rebuild of Google's monthly performance email (the
 * one Randall already reads), same metrics and same month-over-month deltas, so the email
 * stops being the only place those numbers live.
 *
 * A surface without credentials renders a setup card naming the exact env vars it still
 * needs — never an error, and never an empty chart that reads as "a quiet month".
 * Nothing on this screen writes to Google: syncs pull, and the only publish path in the
 * module (replying to a review) lives in the Reviews module behind a confirm step.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Loader2, RefreshCw, AlertCircle, PhoneCall, Navigation, MousePointerClick, Eye,
  Search, MessageSquare, Star, Pin, Megaphone, ExternalLink, Lightbulb, Clock,
  Users, Download, ShieldAlert, CheckCircle2,
} from 'lucide-react';

const API = '/api';
const HG_GREEN = '#48B64C';
const HG_GREEN_LIGHT = '#76BB40';
const HG_BLUE = '#5b9bd5';
const HG_AMBER = '#d9a441';
const HG_RED = '#e07070';

// The first tab is the Money view (Randall, 10 Sep 2026). Its id stays `overview` so every
// existing deep link (?view=overview) still lands on it.
const TABS = [
  { id: 'overview', label: 'Money' },
  { id: 'gbp', label: 'Business Profile' },
  { id: 'ads', label: 'Google Ads' },
  { id: 'seo', label: 'SEO' },
];

// What each surface is waiting on, in the order the applications actually clear.
const SETUP_HELP = {
  gbp: {
    title: 'Business Profile API — pending access',
    body: 'Needs the Basic API Access application approved on the hg-hub-google Cloud project. '
      + 'Until Google approves it the project quota is 0 QPM and every call fails on quota, '
      + 'not on auth — so a failed sync here usually means "still waiting", not "broken".',
  },
  reviews: {
    title: 'Google reviews — pending the same approval',
    body: 'Reviews ride the same Business Profile credentials. Once approved they appear in '
      + 'the Reviews module under a Google tab.',
  },
  ads: {
    title: 'Google Ads API — pending a developer token',
    body: 'The developer token comes from a Manager (MCC) account’s API Center. New tokens '
      + 'start at Test Account Access; reporting on the real account needs Basic (about five '
      + 'business days) or Explorer, whichever lands first.',
  },
  gsc: {
    title: 'Search Console — needs a verified property',
    body: 'No application required. Add henry-griffitts.com as a DOMAIN property, verify with '
      + 'the DNS TXT record, and this tab starts filling. A property has no history before '
      + 'verification, so data accumulates from that day forward.',
  },
};

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const fmtMoney = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(2)}%`);
const shortDate = (d) => (d ? d.slice(5) : '');

function fmtWhen(iso) {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

const SOURCE_LABEL = { gbp: 'Business Profile', reviews: 'Google reviews', ads: 'Google Ads', gsc: 'Search Console' };

/** Why a chart has nothing to draw. Every empty chart says this — a blank frame reads as a
 *  quiet month, and "never synced" / "waiting on Google" / "nothing happened" are three
 *  different things to do about it. */
function whyEmpty(source, config, what) {
  const name = SOURCE_LABEL[source] || source;
  const last = config?.last_syncs?.[source];
  if (!last) return `Empty because ${name} has never synced — press Sync now.`;
  if (!last.ok) {
    return /quota|0 QPM|429/i.test(last.error || '')
      ? `Empty because Google hasn't approved ${name} API access yet — every sync fails on quota 0 until it does.`
      : `Empty because the last ${name} sync failed: ${last.error || 'no reason was recorded'}.`;
  }
  return `Empty because Google reported no ${what} in this range (last synced ${fmtWhen(last.started_at)}).`;
}

// Opaque colours for the new Money-view states: the Hub is a LIGHT theme, where the
// dark-theme amber-300 renders pale on pale.
const OK_PILL = 'text-[#1f7a33] border-[#48B64C] bg-[#48B64C]/10';
const WAIT_PILL = 'text-[#8a5a00] border-[#b7791f] bg-[#b7791f]/10';

// --- shared bits ---------------------------------------------------------------------

function DeltaChip({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-[10px] font-sans text-hg-text-dim" title="No previous month to compare against">—</span>;
  }
  const up = pct >= 0;
  return (
    <span className={`text-[10px] font-sans rounded-sm px-1.5 py-0.5 border ${
      up ? 'text-hg-green border-hg-green/40 bg-hg-green/10' : 'text-red-400 border-red-500/40 bg-red-500/10'
    }`}>
      {up ? '+' : ''}{pct}%
    </span>
  );
}

function StatCard({ icon: Icon, label, value, delta, sub, accent = HG_GREEN, testid }) {
  return (
    <div data-testid={testid} className="bg-hg-surface border border-hg-border rounded-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">{label}</div>
          <div className="font-display text-2xl text-hg-text tracking-wider mt-2">{value}</div>
          <div className="flex items-center gap-2 mt-1.5">
            {delta !== undefined && <DeltaChip pct={delta} />}
            {sub && <span className="text-[11px] text-hg-text-dim font-sans">{sub}</span>}
          </div>
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: `${accent}15` }}>
            <Icon size={15} style={{ color: accent }} strokeWidth={1.5} />
          </div>
        )}
      </div>
    </div>
  );
}

function SetupCard({ surface, cfg, lastSync }) {
  const help = SETUP_HELP[surface] || {};
  const quota = lastSync && lastSync.error && /quota|0 QPM/i.test(lastSync.error);
  return (
    <div data-testid={`google-setup-${surface}`} className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <AlertCircle size={15} className="text-amber-300" strokeWidth={1.5} />
        <span className="font-display text-sm text-hg-text tracking-wider">{help.title}</span>
      </div>
      <p className="text-[13px] text-hg-text-dim font-sans leading-relaxed">{help.body}</p>
      {cfg && cfg.missing && cfg.missing.length > 0 && (
        <div className="text-[11px] font-sans text-hg-text-dim">
          Still unset:{' '}
          {cfg.missing.map((m) => (
            <code key={m} className="text-hg-text bg-hg-bg border border-hg-border rounded-sm px-1.5 py-0.5 mr-1.5 inline-block mt-1">{m}</code>
          ))}
        </div>
      )}
      {quota && (
        <div className="text-[11px] font-sans text-amber-300">
          Last sync failed on quota — that is the pre-approval 0 QPM state, not a bug in the Hub.
        </div>
      )}
    </div>
  );
}

function Section({ title, note, children, right }) {
  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-sm text-hg-text tracking-wider uppercase">{title}</h3>
          {note && <p className="text-[11px] text-hg-text-dim font-sans mt-1">{note}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ children }) {
  return <div className="text-center py-8 text-sm text-hg-text-dim font-sans">{children}</div>;
}

function Table({ columns, rows, testid, empty }) {
  if (!rows.length) return <EmptyRow>{empty}</EmptyRow>;
  return (
    <div className="overflow-x-auto border border-hg-border rounded-sm">
      <table data-testid={testid} className="w-full text-sm font-sans">
        <thead>
          <tr className="bg-hg-surface">
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-wider text-hg-text-dim font-normal ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r._key || i} className="border-t border-hg-border">
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 text-hg-text/90 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function chartAxes() {
  return { stroke: '#666', fontSize: 10 };
}

function ChartFrame({ children, height = 240 }) {
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </div>
  );
}

const TOOLTIP_STYLE = {
  contentStyle: { background: '#161616', border: '1px solid #2a2a2a', borderRadius: 2, fontSize: 12 },
  labelStyle: { color: '#999' },
};

// --- Overview -------------------------------------------------------------------------

const OVERVIEW_ICONS = {
  interactions: Star, calls: PhoneCall, direction_requests: Navigation,
  website_clicks: MousePointerClick, profile_views: Eye, conversations: MessageSquare,
  searches: Search,
};

// One action per playbook loop. A loop with nothing to offer says why, rather than
// disappearing — "no junk terms this week" is a result, not an empty slot.
function WeekActionsCard({ data, onNavigate }) {
  if (!data?.actions?.length) return null;
  return (
    <div data-testid="google-week-actions" className="bg-hg-surface border border-hg-green/30 rounded-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Lightbulb size={14} className="text-hg-green" strokeWidth={1.5} />
        <span className="font-display text-sm text-hg-text tracking-wider uppercase">This week's actions</span>
      </div>
      <div className="space-y-2">
        {data.actions.map((a) => (
          <div key={a.loop} data-testid={`week-action-${a.loop}`}
            className={`flex items-start gap-3 border rounded-sm px-3 py-2.5 ${
              a.blocked ? 'border-hg-border opacity-70' : 'border-hg-border hover:border-hg-green/50'
            }`}>
            <div className="min-w-0 flex-1">
              <div className={`text-[13px] font-sans ${a.blocked ? 'text-hg-text-dim' : 'text-hg-text'}`}>
                {a.title}
              </div>
              <div className="text-[11px] font-sans text-hg-text-dim mt-0.5 break-words">{a.detail}</div>
            </div>
            {!a.blocked && (
              <button
                onClick={() => onNavigate?.(a.module, a.tab ? { tab: a.tab } : undefined)}
                className="text-[11px] font-sans text-hg-green border border-hg-green/40 rounded-sm px-2 py-1 hover:bg-hg-green/10 flex-shrink-0">
                Open
              </button>
            )}
            {a.remaining > 0 && (
              <span className="text-[10px] font-sans text-hg-text-dim flex-shrink-0 self-center">
                +{a.remaining} more
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="text-[10px] font-sans text-hg-text-dim">{data.note}</div>
    </div>
  );
}

// --- Money ------------------------------------------------------------------------------

/** What still stands between the Hub and real numbers — one row per blocker, each with the
 *  live status its source reported, who owns it, and the one action that clears it. */
function BlockersCard({ blockers, onFlip, flipping }) {
  return (
    <div data-testid="google-blockers" className="bg-hg-surface border border-[#b7791f]/50 rounded-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldAlert size={14} className="text-[#8a5a00]" strokeWidth={1.5} />
        <span className="font-display text-sm text-hg-text tracking-wider uppercase">What's blocking real numbers</span>
      </div>
      <div className="space-y-2">
        {(blockers || []).map((b) => (
          <div key={b.key} data-testid={`blocker-${b.key}`} className="flex items-start gap-3 border border-hg-border rounded-sm px-3 py-2.5">
            {b.ok
              ? <CheckCircle2 size={14} className="text-[#1f7a33] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              : <AlertCircle size={14} className="text-[#8a5a00] flex-shrink-0 mt-0.5" strokeWidth={1.5} />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-sans text-hg-text">{b.title}</span>
                <span data-testid={`blocker-status-${b.key}`}
                      className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${b.ok ? OK_PILL : WAIT_PILL}`}>
                  {b.status}
                </span>
                <span className="text-[10px] font-sans text-hg-text-dim">Owner: {b.owner}</span>
              </div>
              <div className="text-[11px] font-sans text-hg-text-dim mt-0.5 break-words">{b.action}</div>
              {b.detail && <div className="text-[11px] font-sans text-hg-text-dim mt-0.5 break-words">{b.detail}</div>}
            </div>
            {b.key === 'site_conversion_tracking_live' && (
              <button data-testid="flip-tracking" disabled={flipping} onClick={() => onFlip(!b.ok)}
                className="text-[11px] font-sans text-hg-text border border-hg-border rounded-sm px-2 py-1 hover:border-hg-green flex-shrink-0 disabled:opacity-40">
                {b.ok ? 'Mark not live' : 'Mark live'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MoneyView({ reload }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [flipping, setFlipping] = useState(false);
  const [negatives, setNegatives] = useState(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await axios.get(`${API}/google/money`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load the Money view.'); }
  }, []);

  useEffect(() => { load(); }, [load, reload]);

  const flip = async (live) => {
    setFlipping(true);
    try {
      await axios.post(`${API}/google/flags/site_conversion_tracking_live`, { live });
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not change the tracking flag.'); }
    setFlipping(false);
  };

  // A text list to paste into a negative keyword list. The Hub never writes to Ads.
  const exportNegatives = async () => {
    try {
      const r = await axios.get(`${API}/google/negatives.txt`, { responseType: 'text' });
      setNegatives(typeof r.data === 'string' ? r.data : '');
    } catch (e) { setErr(e.response?.data?.detail || 'Could not build the negatives list.'); }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([negatives || ''], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'hg-negative-keywords.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  if (err && !data) return <div className="text-xs text-red-400 font-sans" data-testid="google-money-error">{err}</div>;
  if (!data) return <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;

  const m = data;
  const junk = m.junk_terms || [];
  return (
    <div className="space-y-6" data-testid="google-money">
      <Section title={`Money — ${m.month}`} note={m.cpl?.note}>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard testid="money-spend" icon={Megaphone} label="Ads spend this month" accent={HG_AMBER}
            value={m.ads?.configured ? fmtMoney(m.ads.spend) : '—'}
            sub={!m.ads?.configured ? 'Google Ads not connected' : (m.ads.note || `${fmt(m.ads.clicks)} clicks`)} />
          <StatCard testid="money-google-leads" icon={Users} label="Google leads" value={fmt(m.leads?.google)}
            sub={`in the Leads Tracker — ${m.leads?.rule}`} />
          <StatCard testid="money-google-cpl" label="Google CPL" accent={HG_BLUE} value={fmtMoney(m.cpl?.google)}
            sub={m.leads?.google ? `${fmtMoney(m.ads?.spend)} ÷ ${fmt(m.leads.google)} leads` : 'no Google leads yet this month'} />
          <StatCard testid="money-meta-cpl" label="Meta CPL" accent={HG_BLUE} value={fmtMoney(m.cpl?.meta)}
            sub={m.cpl?.meta_error || `${fmtMoney(m.cpl?.meta_spend)} ÷ ${fmt(m.leads?.meta)} Meta leads`} />
          <StatCard testid="money-conversions" label="Conversions" accent={HG_BLUE} value={fmt(m.conversions?.count)}
            sub={m.conversions?.label} />
        </div>
      </Section>

      {/* The Hub's own Meta ADS (Randall, 18 Sep 2026) — the recruiting films. Clicks are not
          leads (5 Oct 2026): link clicks and applications are separate cards, CPL is spend per
          APPLICATION, and with none it says so — never a dash, never a per-click figure. */}
      {m.meta_ads && (
        <Section title="Meta ads — recruiting" note={m.meta_ads.rule}>
          {(m.meta_ads.issues || []).map((i) => (
            <div key={i.draft_id} data-testid={`money-meta-ad-issue-${i.draft_id}`}
                 className="mb-3 rounded-sm p-3 text-xs font-sans" style={{ background: '#FDECEA', color: '#8A1C12' }}>
              <strong>{i.headline || i.slug}: {i.label} ({i.status})</strong> · seen {String(i.seen_at || '').slice(0, 10)}
              {(i.reasons || []).map((r) => <div key={r}>{r}</div>)}
            </div>
          ))}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="money-meta-ads">
            <StatCard testid="money-meta-ads-spend" icon={Megaphone} label="Meta ads spend this month" accent={HG_AMBER}
              value={m.meta_ads.error ? '—' : fmtMoney(m.meta_ads.spend)}
              sub={m.meta_ads.error ? `Unreadable: ${m.meta_ads.error}`
                : (!m.meta_ads.connected ? 'Meta Ads not connected (Settings → API status)'
                  : `${fmt(m.meta_ads.ads)} ad${m.meta_ads.ads === 1 ? '' : 's'} · synced ${String(m.meta_ads.last_synced || 'never').slice(0, 10)}`)} />
            <StatCard testid="money-meta-ads-clicks" label="Link clicks (not leads)" accent={HG_BLUE}
              value={m.meta_ads.error ? '—' : (m.meta_ads.link_clicks == null ? 'Not synced yet' : fmt(m.meta_ads.link_clicks))}
              sub={m.meta_ads.error ? `Unreadable: ${m.meta_ads.error}`
                : `${m.meta_ads.cost_per_click != null ? `${fmtMoney(m.meta_ads.cost_per_click)} per click · ` : ''}Meta's count this month · ${fmt(m.meta_ads.landing_clicks)} counted on the landing links in flight`} />
            <StatCard testid="money-meta-ads-applications" icon={Users} label="Applications"
              value={m.meta_ads.error ? '—' : (m.meta_ads.applications == null ? 'Not attributable' : fmt(m.meta_ads.applications))}
              sub={m.meta_ads.error ? `Unreadable: ${m.meta_ads.error}` : "Become-a-Fitter applications carrying an ad's tag, this month"} />
            <StatCard testid="money-meta-ads-cpl" label="Cost per application" accent={m.meta_ads.cpl == null && !m.meta_ads.error ? HG_AMBER : HG_BLUE}
              value={m.meta_ads.error ? '—' : (m.meta_ads.cpl != null ? fmtMoney(m.meta_ads.cpl)
                : (m.meta_ads.applications === 0 ? 'No applications yet' : 'Not attributable'))}
              sub={m.meta_ads.error ? `Unreadable: ${m.meta_ads.error}`
                : (m.meta_ads.applications ? `${fmtMoney(m.meta_ads.spend)} ÷ ${fmt(m.meta_ads.applications)} applications`
                  : `${fmtMoney(m.meta_ads.spend)} spent this month, ${m.meta_ads.applications === 0 ? '0 applications' : 'applications cannot be tied to these ads'}`)} />
            <StatCard testid="money-meta-ads-vs-google" label="Google CPL (for comparison)" accent={HG_BLUE} value={fmtMoney(m.cpl?.google)}
              sub="this month, Leads Tracker basis" />
          </div>
        </Section>
      )}

      {/* The Instagram link-in-bio page (Randall, 22 Sep 2026): Instagram captions say "link in
          bio", and every press on the /ig page's buttons is counted here, by button. Clicks, not
          leads — no spend and no form sit on this row. */}
      {m.instagram_bio && (
        <Section title="Instagram bio" note={`Clicks through ${m.instagram_bio.page} this month (crawlers excluded).`}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="money-instagram-bio">
            <StatCard testid="money-ig-bio-clicks" icon={Users} label="Instagram bio clicks"
              value={m.instagram_bio.error ? '—' : fmt(m.instagram_bio.clicks)}
              sub={m.instagram_bio.error ? `Unreadable: ${m.instagram_bio.error}` : 'all four buttons'} />
            <StatCard testid="money-ig-bio-find" label="Find a fitter" accent={HG_BLUE}
              value={m.instagram_bio.error ? '—' : fmt(m.instagram_bio.by_button?.['find-a-fitter'])} sub="to the fitter locator" />
            <StatCard testid="money-ig-bio-become" label="Become a fitter" accent={HG_BLUE}
              value={m.instagram_bio.error ? '—' : fmt(m.instagram_bio.by_button?.['become-a-fitter'])} sub="to the Become-a-Fitter page" />
            <StatCard testid="money-ig-bio-film" label="Latest film" accent={HG_BLUE}
              value={m.instagram_bio.error ? '—' : fmt(m.instagram_bio.by_button?.film)} sub="plays of the newest library film" />
            <StatCard testid="money-ig-bio-partners" label="Facility partners" accent={HG_BLUE}
              value={m.instagram_bio.error ? '—' : fmt(m.instagram_bio.by_button?.partners)} sub="to the partner page" />
          </div>
        </Section>
      )}

      <BlockersCard blockers={m.blockers} onFlip={flip} flipping={flipping} />
      {err && <div className="text-xs text-red-400 font-sans">{err}</div>}

      <Section
        title="Junk-term candidates"
        note={`${m.junk_rule}. Export gives a plain text list to paste into a negative keyword list in Google Ads — the Hub never writes to Ads.`}
        right={(
          <button data-testid="export-negatives" disabled={!junk.length} onClick={exportNegatives}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
            <Download size={12} /> Export negatives
          </button>
        )}
      >
        <Table
          testid="money-junk"
          empty={m.ads?.configured ? `No term has ${m.junk_rule}.` : 'Google Ads is not connected, so there are no search terms to judge yet.'}
          rows={junk.map((t) => ({ ...t, _key: t.term }))}
          columns={[
            { key: 'term', label: 'Term' },
            { key: 'clicks', label: 'Clicks', align: 'right', render: (r) => fmt(r.clicks) },
            {
              key: 'cost', label: 'Cost', align: 'right',
              render: (r) => (r.cost === null || r.cost === undefined
                ? <span className="text-hg-text-dim" title="Performance Max reports no per-term cost">n/a</span>
                : fmtMoney(r.cost)),
            },
            { key: 'source', label: 'From', render: (r) => (r.source === 'pmax_category' ? 'PMax category' : 'Search term') },
          ]}
        />
        {negatives !== null && (
          <div className="space-y-2" data-testid="negatives-export">
            <textarea readOnly value={negatives} rows={Math.min(12, negatives.split('\n').length + 1)}
              className="w-full bg-hg-bg border border-hg-border rounded-sm p-2 text-xs text-hg-text font-mono" />
            <div className="flex gap-2">
              <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(negatives)}
                className="text-[11px] font-sans border border-hg-border rounded-sm px-2 py-1 text-hg-text hover:border-hg-green">Copy</button>
              <button onClick={download}
                className="text-[11px] font-sans border border-hg-border rounded-sm px-2 py-1 text-hg-text hover:border-hg-green">Download .txt</button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

function OverviewTab({ location, config, reload, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const p = new URLSearchParams();
      if (location) p.set('location', location);
      const r = await axios.get(`${API}/google/overview?${p.toString()}`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load the Google overview.'); }
    setLoading(false);
  }, [location]);

  useEffect(() => { load(); }, [load, reload]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  if (err) return <div className="text-xs text-red-400 font-sans" data-testid="google-overview-error">{err}</div>;
  if (!data) return null;

  const gbpOk = config?.surfaces?.gbp?.configured;
  const gscOk = config?.surfaces?.gsc?.configured;
  const gsc = data.gsc || {};
  const gscPrev = data.gsc_prev || {};
  const pctDelta = (a, b) => (b ? Math.round(((a - b) / b) * 1000) / 10 : null);

  return (
    <div className="space-y-8" data-testid="google-overview">
      <MoneyView reload={reload} />
      <WeekActionsCard data={data.week_actions} onNavigate={onNavigate} />

      <Section
        title={`Business Profile — ${data.month}`}
        note={data.metric_note}
      >
        {!gbpOk ? (
          <SetupCard surface="gbp" cfg={config?.surfaces?.gbp} lastSync={data.last_syncs?.gbp} />
        ) : !data.gbp.has_data ? (
          <EmptyRow>No Business Profile days synced for {data.month} yet — run a sync.</EmptyRow>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {data.gbp.headline.map((h) => (
              <StatCard
                key={h.key}
                testid={`google-stat-${h.key}`}
                icon={OVERVIEW_ICONS[h.key]}
                label={h.label}
                value={h.is_floor ? `${fmt(h.value)}+` : fmt(h.value)}
                delta={h.delta_pct}
                sub={h.is_floor ? 'at least — some terms reported as a threshold' : `vs ${fmt(h.prev)} in ${data.previous_month}`}
              />
            ))}
          </div>
        )}
      </Section>

      <Section title={`Search Console — ${data.month}`} note="The site's own search performance. Separate from the Business Profile's numbers above.">
        {!gscOk ? (
          <SetupCard surface="gsc" cfg={config?.surfaces?.gsc} lastSync={data.last_syncs?.gsc} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard testid="google-gsc-clicks" icon={MousePointerClick} label="Clicks" value={fmt(gsc.clicks)} delta={pctDelta(gsc.clicks, gscPrev.clicks)} />
            <StatCard icon={Eye} label="Impressions" value={fmt(gsc.impressions)} delta={pctDelta(gsc.impressions, gscPrev.impressions)} />
            <StatCard label="CTR" value={fmtPct(gsc.ctr)} accent={HG_BLUE} />
            <StatCard label="Avg position" value={gsc.avg_position ?? '—'} accent={HG_BLUE} />
          </div>
        )}
      </Section>

      <Section title="Google reviews" note="Full list and replies live in the Reviews module.">
        {!config?.surfaces?.reviews?.configured ? (
          <SetupCard surface="reviews" cfg={config?.surfaces?.reviews} lastSync={data.last_syncs?.reviews} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Star} label="Reviews" value={fmt(data.reviews?.total)} />
            <StatCard label="Average" value={data.reviews?.average ?? '—'} />
            <StatCard label="Unreplied" value={fmt(data.reviews?.unreplied)} accent={data.reviews?.unreplied ? HG_AMBER : HG_GREEN} />
          </div>
        )}
      </Section>
    </div>
  );
}

// --- Business Profile ------------------------------------------------------------------

function GbpTab({ location, config, reload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const end = new Date();
      const start = new Date(end.getTime() - days * 86400000);
      const p = new URLSearchParams({ start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
      if (location) p.set('location', location);
      const r = await axios.get(`${API}/google/gbp?${p.toString()}`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load Business Profile data.'); }
    setLoading(false);
  }, [location, days]);

  useEffect(() => { load(); }, [load, reload]);

  if (!config?.surfaces?.gbp?.configured) {
    return <SetupCard surface="gbp" cfg={config?.surfaces?.gbp} lastSync={config?.last_syncs?.gbp} />;
  }
  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  if (err) return <div className="text-xs text-red-400 font-sans">{err}</div>;
  if (!data) return null;

  const series = data.series || [];
  const cur = data.month_totals || {};
  const prev = data.prev_month_totals || {};
  const compare = [
    ['Interactions', cur.interactions, prev.interactions],
    ['Calls', cur.calls, prev.calls],
    ['Direction requests', cur.direction_requests, prev.direction_requests],
    ['Website clicks', cur.website_clicks, prev.website_clicks],
    ['Profile views', cur.profile_views, prev.profile_views],
    ['Searches', data.month_searches?.searches, data.prev_month_searches?.searches],
  ];

  return (
    <div className="space-y-8" data-testid="google-gbp">
      <div className="flex gap-1.5 flex-wrap">
        {[30, 90, 365].map((d) => (
          <button key={d} data-testid={`gbp-range-${d}`} onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
              days === d ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
            }`}>
            {d === 365 ? 'Last 12 months' : `Last ${d} days`}
          </button>
        ))}
      </div>

      <Section title="Actions people took" note="Calls, direction requests and website clicks — the interactions Google counts.">
        {series.length === 0 ? <EmptyRow>{whyEmpty('gbp', config, 'Business Profile days')}</EmptyRow> : (
          <ChartFrame>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#242424" />
              <XAxis dataKey="date" tickFormatter={shortDate} {...chartAxes()} />
              <YAxis {...chartAxes()} />
              <Tooltip {...TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="calls" name="Calls" stroke={HG_GREEN} dot={false} strokeWidth={1.5} />
              <Line type="monotone" dataKey="direction_requests" name="Directions" stroke={HG_BLUE} dot={false} strokeWidth={1.5} />
              <Line type="monotone" dataKey="website_clicks" name="Website" stroke={HG_GREEN_LIGHT} dot={false} strokeWidth={1.5} />
            </LineChart>
          </ChartFrame>
        )}
      </Section>

      <Section title="Profile views by surface" note="Maps and Search, desktop and mobile — the four metrics that sum to 'profile views'.">
        {series.length === 0 ? <EmptyRow>{whyEmpty('gbp', config, 'Business Profile days')}</EmptyRow> : (
          <ChartFrame>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#242424" />
              <XAxis dataKey="date" tickFormatter={shortDate} {...chartAxes()} />
              <YAxis {...chartAxes()} />
              <Tooltip {...TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="profile_views_mobile_search" name="Mobile · Search" stackId="v" fill={HG_GREEN} />
              <Bar dataKey="profile_views_mobile_maps" name="Mobile · Maps" stackId="v" fill={HG_GREEN_LIGHT} />
              <Bar dataKey="profile_views_desktop_search" name="Desktop · Search" stackId="v" fill={HG_BLUE} />
              <Bar dataKey="profile_views_desktop_maps" name="Desktop · Maps" stackId="v" fill={HG_AMBER} />
            </BarChart>
          </ChartFrame>
        )}
      </Section>

      <Section title={`${data.month} vs ${data.previous_month}`} note="The month-over-month comparison in the same shape as Google's email.">
        <Table
          testid="gbp-compare"
          empty="Nothing synced for either month yet."
          rows={compare.filter((c) => c[1] !== undefined).map(([label, a, b]) => ({ _key: label, label, a, b }))}
          columns={[
            { key: 'label', label: 'Metric' },
            { key: 'a', label: data.month, align: 'right', render: (r) => fmt(r.a) },
            { key: 'b', label: data.previous_month, align: 'right', render: (r) => fmt(r.b) },
            { key: 'd', label: 'Change', align: 'right', render: (r) => <DeltaChip pct={r.b ? Math.round(((r.a - r.b) / r.b) * 1000) / 10 : null} /> },
          ]}
        />
      </Section>

      <Section
        title={`Search terms — ${data.month}`}
        note={'How people searched before the profile appeared. Google reports small counts as "fewer than N" rather than an exact number; those render as "<N".'}
      >
        <Table
          testid="gbp-keywords"
          empty={`No keywords synced for ${data.month} yet.`}
          rows={(data.keywords || []).slice(0, 100).map((k) => ({ ...k, _key: k.keyword }))}
          columns={[
            { key: 'keyword', label: 'Search term' },
            {
              key: 'impressions', label: 'Impressions', align: 'right',
              render: (r) => (r.is_threshold ? `<${fmt(r.impressions)}` : fmt(r.impressions)),
            },
            {
              key: 'prev_impressions', label: data.previous_month, align: 'right',
              render: (r) => (r.prev_impressions === null || r.prev_impressions === undefined ? '—'
                : (r.prev_is_threshold ? `<${fmt(r.prev_impressions)}` : fmt(r.prev_impressions))),
            },
            {
              key: 'delta', label: 'Change', align: 'right',
              render: (r) => (r.is_new
                ? <span className="text-[10px] font-sans text-hg-green border border-hg-green/40 rounded-sm px-1.5 py-0.5">new</span>
                : <DeltaChip pct={r.delta_pct} />),
            },
          ]}
        />
      </Section>
    </div>
  );
}

// --- Google Ads -------------------------------------------------------------------------

function AdsTab({ config, reload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const end = new Date();
      const start = new Date(end.getTime() - days * 86400000);
      const p = new URLSearchParams({ start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
      const r = await axios.get(`${API}/google/ads?${p.toString()}`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load Google Ads data.'); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load, reload]);

  if (!config?.surfaces?.ads?.configured) {
    return <SetupCard surface="ads" cfg={config?.surfaces?.ads} lastSync={config?.last_syncs?.ads} />;
  }
  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  if (err) return <div className="text-xs text-red-400 font-sans">{err}</div>;
  if (!data) return null;

  const t = data.totals || {};
  const leads = data.leads || {};

  return (
    <div className="space-y-8" data-testid="google-ads">
      <div className="flex gap-1.5 flex-wrap">
        {[7, 30, 90, 365].map((d) => (
          <button key={d} data-testid={`ads-range-${d}`} onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
              days === d ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
            }`}>
            {d === 365 ? 'Last 12 months' : `Last ${d} days`}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Megaphone} label="Spend" value={fmtMoney(t.cost)} accent={HG_AMBER} />
        <StatCard icon={MousePointerClick} label="Clicks" value={fmt(t.clicks)} sub={`CTR ${fmtPct(t.ctr)}`} />
        <StatCard label="Avg CPC" value={fmtMoney(t.avg_cpc)} accent={HG_BLUE} />
        <StatCard label="Conversions" value={fmt(t.conversions)} sub={`${fmtMoney(t.cost_per_conversion)} each`} accent={HG_BLUE} />
      </div>

      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 text-[11px] font-sans text-hg-text-dim">
        <span className="text-hg-text">Cost per lead: {leads.cost_per_lead ? fmtMoney(leads.cost_per_lead) : '—'}</span>
        {' '}· {fmt(leads.hub_leads)} Google leads in the Leads Tracker · {leads.note}
      </div>

      <Section title="Spend" note="Daily cost across every campaign in the range.">
        {(data.series || []).length === 0 ? <EmptyRow>{whyEmpty('ads', config, 'campaign days')}</EmptyRow> : (
          <ChartFrame>
            <LineChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#242424" />
              <XAxis dataKey="date" tickFormatter={shortDate} {...chartAxes()} />
              <YAxis {...chartAxes()} />
              <Tooltip {...TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="cost" name="Spend ($)" stroke={HG_AMBER} dot={false} strokeWidth={1.5} />
              <Line type="monotone" dataKey="clicks" name="Clicks" stroke={HG_GREEN} dot={false} strokeWidth={1.5} />
            </LineChart>
          </ChartFrame>
        )}
      </Section>

      <Section title="Campaigns">
        <Table
          testid="ads-campaigns"
          empty="No campaigns reported any activity in this range."
          rows={(data.campaigns || []).map((c) => ({ ...c, _key: c.campaign_id }))}
          columns={[
            { key: 'campaign_name', label: 'Campaign' },
            {
              key: 'campaign_status', label: 'Status',
              render: (r) => (
                <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${
                  r.campaign_status === 'ENABLED' ? 'text-hg-green border-hg-green/40 bg-hg-green/10' : 'text-hg-text-dim border-hg-border'
                }`}>{(r.campaign_status || '').toLowerCase() || '—'}</span>
              ),
            },
            { key: 'impressions', label: 'Impr.', align: 'right', render: (r) => fmt(r.impressions) },
            { key: 'clicks', label: 'Clicks', align: 'right', render: (r) => fmt(r.clicks) },
            { key: 'ctr', label: 'CTR', align: 'right', render: (r) => fmtPct(r.ctr) },
            { key: 'avg_cpc', label: 'Avg CPC', align: 'right', render: (r) => fmtMoney(r.avg_cpc) },
            { key: 'cost', label: 'Cost', align: 'right', render: (r) => fmtMoney(r.cost) },
            { key: 'conversions', label: 'Conv.', align: 'right', render: (r) => fmt(r.conversions) },
          ]}
        />
      </Section>

      <Section
        title="Search terms"
        note="What people actually typed. A term with spend and no conversions is flagged as a negative-keyword candidate — a suggestion only; the Hub cannot change the account."
      >
        <Table
          testid="ads-search-terms"
          empty="No search terms in this range."
          rows={(data.search_terms || []).map((s) => ({ ...s, _key: s.search_term }))}
          columns={[
            {
              key: 'search_term', label: 'Term',
              render: (r) => (
                <span className="flex items-center gap-2">
                  {r.search_term}
                  {r.negative_candidate && (
                    <span className="text-[9px] font-sans uppercase tracking-wider text-amber-300 border border-amber-400/40 rounded-sm px-1.5 py-0.5"
                      title="Spend with no conversions — review as a negative keyword">
                      review as negative
                    </span>
                  )}
                </span>
              ),
            },
            { key: 'impressions', label: 'Impr.', align: 'right', render: (r) => fmt(r.impressions) },
            { key: 'clicks', label: 'Clicks', align: 'right', render: (r) => fmt(r.clicks) },
            { key: 'cost', label: 'Cost', align: 'right', render: (r) => fmtMoney(r.cost) },
            { key: 'conversions', label: 'Conv.', align: 'right', render: (r) => fmt(r.conversions) },
          ]}
        />
        {data.search_terms_truncated && (
          <div className="text-[11px] font-sans text-hg-text-dim">Showing the 250 highest-spend terms — the range holds more.</div>
        )}
        {(data.search_terms || []).length === 0 && (data.pmax_categories?.rows || []).length > 0 && (
          <div className="text-[11px] font-sans text-hg-text-dim">
            This table is empty because the account runs Performance Max, which reports nothing here — its
            equivalent is below.
          </div>
        )}
      </Section>

      {(data.pmax_categories?.rows || []).length > 0 && (
        <Section
          title="Performance Max search categories"
          note={data.pmax_categories.note}
          right={data.pmax_categories.period && (
            <span className="text-[11px] font-sans text-hg-text-dim">
              {data.pmax_categories.period.start} → {data.pmax_categories.period.end}
            </span>
          )}
        >
          <Table
            testid="ads-pmax-categories"
            empty="No categories in the latest window."
            rows={(data.pmax_categories.rows || []).map((c) => ({ ...c, _key: c.category_label }))}
            columns={[
              {
                key: 'category_label', label: 'Category',
                render: (r) => (
                  <span className="flex items-center gap-2">
                    <span className={r.is_other ? 'text-hg-text-dim italic' : ''}>{r.category_label}</span>
                    {r.is_other && (
                      <span className="text-[9px] font-sans uppercase tracking-wider text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5"
                        title="Google's catch-all bucket — not a real search category, never a negative-keyword candidate">
                        google's bucket
                      </span>
                    )}
                    {r.review_candidate && (
                      <span className="text-[9px] font-sans uppercase tracking-wider text-amber-300 border border-amber-400/40 rounded-sm px-1.5 py-0.5"
                        title="Clicks with no conversions — worth a look as an account-level negative">
                        review as negative
                      </span>
                    )}
                  </span>
                ),
              },
              { key: 'impressions', label: 'Impr.', align: 'right', render: (r) => fmt(r.impressions) },
              { key: 'clicks', label: 'Clicks', align: 'right', render: (r) => fmt(r.clicks) },
              { key: 'conversions', label: 'Conv.', align: 'right', render: (r) => fmt(r.conversions) },
              {
                key: 'cost', label: 'Cost', align: 'right',
                render: () => <span className="text-hg-text-dim" title="Google prohibits cost on this report">n/a</span>,
              },
            ]}
          />
        </Section>
      )}
    </div>
  );
}

// --- SEO ---------------------------------------------------------------------------------

function SeoTab({ config, reload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(28);
  const [err, setErr] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [bump, setBump] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const end = new Date(Date.now() - 2 * 86400000);   // GSC lags ~2 days
      const start = new Date(end.getTime() - (days - 1) * 86400000);
      const p = new URLSearchParams({ start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
      const r = await axios.get(`${API}/google/seo?${p.toString()}`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load Search Console data.'); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load, reload, bump]);

  const togglePin = async (query, pinned) => {
    setPinBusy(true);
    try {
      if (pinned) await axios.delete(`${API}/google/seo/pins?query=${encodeURIComponent(query)}`);
      else await axios.post(`${API}/google/seo/pins`, { query });
      setBump((b) => b + 1);
    } catch { /* pinning is cosmetic — never block the tab on it */ }
    setPinBusy(false);
  };

  if (!config?.surfaces?.gsc?.configured) {
    return <SetupCard surface="gsc" cfg={config?.surfaces?.gsc} lastSync={config?.last_syncs?.gsc} />;
  }
  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  if (err) return <div className="text-xs text-red-400 font-sans">{err}</div>;
  if (!data) return null;

  const t = data.totals || {};
  const p = data.prev_totals || {};
  const pctDelta = (a, b) => (b ? Math.round(((a - b) / b) * 1000) / 10 : null);
  const opp = data.opportunities || {};
  const pinned = new Set(data.pinned || []);
  const pinChart = (() => {
    const byDate = {};
    Object.entries(data.pin_series || {}).forEach(([kw, rows]) => {
      rows.forEach((r) => { byDate[r.date] = { ...(byDate[r.date] || { date: r.date }), [kw]: r.position }; });
    });
    return Object.values(byDate).sort((a, b) => (a.date < b.date ? -1 : 1));
  })();
  const pinColors = [HG_GREEN, HG_BLUE, HG_AMBER, HG_GREEN_LIGHT, HG_RED];

  return (
    <div className="space-y-8" data-testid="google-seo">
      <div className="flex gap-1.5 flex-wrap items-center">
        {[7, 28, 90].map((d) => (
          <button key={d} data-testid={`seo-range-${d}`} onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
              days === d ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
            }`}>
            Last {d} days
          </button>
        ))}
        <span className="text-[11px] font-sans text-hg-text-dim ml-auto">{data.lag_note}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={MousePointerClick} label="Clicks" value={fmt(t.clicks)} delta={pctDelta(t.clicks, p.clicks)} />
        <StatCard icon={Eye} label="Impressions" value={fmt(t.impressions)} delta={pctDelta(t.impressions, p.impressions)} />
        <StatCard label="CTR" value={fmtPct(t.ctr)} accent={HG_BLUE} />
        <StatCard label="Avg position" value={t.avg_position ?? '—'} accent={HG_BLUE} />
      </div>

      <Section title="Opportunities" note="Computed from the same data, display only — nothing here changes a page or a bid.">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[
            ['page_two_keywords', opp.page_two_keywords, (r) => `${r.query} · position ${r.position} · ${fmt(r.impressions)} impressions`],
            ['low_ctr_pages', opp.low_ctr_pages, (r) => `${r.page} · ${fmt(r.impressions)} impressions · ${fmtPct(r.ctr)} CTR`],
            ['profile_vs_site', opp.profile_vs_site, (r) => `${r.keyword} · ${fmt(r.impressions)} profile impressions (${r.month})`],
          ].map(([key, rows, line]) => (
            <div key={key} data-testid={`seo-opp-${key}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Lightbulb size={14} className="text-hg-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-[12px] font-sans text-hg-text leading-snug">{opp.labels?.[key]}</span>
              </div>
              {(rows || []).length === 0 ? (
                <div className="text-[11px] font-sans text-hg-text-dim">Nothing qualifies yet in this range.</div>
              ) : (
                <ul className="space-y-1.5">
                  {(rows || []).slice(0, 8).map((r, i) => (
                    <li key={i} className="text-[11px] font-sans text-hg-text-dim break-words">{line(r)}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Pinned queries — position over time" note="Lower is better; the axis is reversed so up means improving.">
        {pinChart.length === 0 ? (
          <EmptyRow>
            {(data.queries || []).length === 0
              ? whyEmpty('gsc', config, 'queries')
              : 'Empty because no query is pinned yet — pin one in Top queries below to chart its position.'}
          </EmptyRow>
        ) : (
          <ChartFrame>
            <LineChart data={pinChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#242424" />
              <XAxis dataKey="date" tickFormatter={shortDate} {...chartAxes()} />
              <YAxis reversed domain={[1, 'dataMax']} {...chartAxes()} />
              <Tooltip {...TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {Object.keys(data.pin_series || {}).map((kw, i) => (
                <Line key={kw} type="monotone" dataKey={kw} name={kw} stroke={pinColors[i % pinColors.length]} dot={false} strokeWidth={1.5} connectNulls />
              ))}
            </LineChart>
          </ChartFrame>
        )}
      </Section>

      <Section title="Top queries" note="Pin a query to track its position over time.">
        <Table
          testid="seo-queries"
          empty="No query data yet — a newly verified property accumulates data from verification day forward."
          rows={(data.queries || []).slice(0, 100).map((q) => ({ ...q, _key: q.query }))}
          columns={[
            {
              key: 'query', label: 'Query',
              render: (r) => (
                <span className="flex items-center gap-2">
                  <button disabled={pinBusy} onClick={() => togglePin(r.query, pinned.has(r.query))}
                    title={pinned.has(r.query) ? 'Unpin' : 'Pin this query'}
                    className={`${pinned.has(r.query) ? 'text-hg-green' : 'text-hg-text-dim hover:text-hg-green'} disabled:opacity-40`}>
                    <Pin size={12} />
                  </button>
                  {r.query}
                </span>
              ),
            },
            { key: 'clicks', label: 'Clicks', align: 'right', render: (r) => fmt(r.clicks) },
            { key: 'impressions', label: 'Impr.', align: 'right', render: (r) => fmt(r.impressions) },
            { key: 'ctr', label: 'CTR', align: 'right', render: (r) => fmtPct(r.ctr) },
            { key: 'position', label: 'Position', align: 'right', render: (r) => (r.position ?? '—') },
            {
              key: 'position_change', label: `${data.range.days}-day move`, align: 'right',
              render: (r) => (r.is_new
                ? <span className="text-[10px] font-sans text-hg-green border border-hg-green/40 rounded-sm px-1.5 py-0.5">new</span>
                : r.position_change === null || r.position_change === undefined ? '—'
                  : <span className={r.position_change > 0 ? 'text-hg-green' : r.position_change < 0 ? 'text-red-400' : 'text-hg-text-dim'}>
                    {r.position_change > 0 ? '↑' : r.position_change < 0 ? '↓' : ''}{Math.abs(r.position_change)}
                  </span>),
            },
          ]}
        />
      </Section>

      <Section title="Top pages">
        <Table
          testid="seo-pages"
          empty="No page data yet."
          rows={(data.pages || []).slice(0, 50).map((p2) => ({ ...p2, _key: p2.page }))}
          columns={[
            {
              key: 'page', label: 'Page',
              render: (r) => (
                <a href={r.page} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-hg-green break-all">
                  {r.page}<ExternalLink size={10} />
                </a>
              ),
            },
            { key: 'clicks', label: 'Clicks', align: 'right', render: (r) => fmt(r.clicks) },
            { key: 'impressions', label: 'Impr.', align: 'right', render: (r) => fmt(r.impressions) },
            { key: 'ctr', label: 'CTR', align: 'right', render: (r) => fmtPct(r.ctr) },
            { key: 'position', label: 'Position', align: 'right', render: (r) => (r.position ?? '—') },
          ]}
        />
      </Section>
    </div>
  );
}

// --- main module ---------------------------------------------------------------------------

export default function GoogleInsights({ onNavigate }) {
  // Dashboard chips deep-link to a specific tab (?module=google&view=ads).
  const [tab, setTab] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('view');
      return TABS.some((t) => t.id === v) ? v : 'overview';
    } catch { return 'overview'; }
  });
  const [config, setConfig] = useState(null);
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [reload, setReload] = useState(0);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/google/config`);
      setConfig(r.data);
    } catch { setConfig({ surfaces: {}, locations: [], last_syncs: {} }); }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Opening this page IS the weekly check-in — recording the visit is what clears that
  // dashboard chip. Fire-and-forget: a failed write must never block the screen.
  useEffect(() => { axios.post(`${API}/google/visited`).catch(() => {}); }, []);

  const syncNow = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await axios.post(`${API}/google/sync/all`, {});
      const results = r.data.results || [];
      // Report each source honestly: a pending approval is not a success, and a run that
      // wrote nothing should never read as a run that worked.
      setSyncMsg(results.map((s) => {
        if (s.skipped) return `${s.source}: not configured`;
        if (!s.ok) return `${s.source}: failed — ${s.error}`;
        return `${s.source}: ${s.rows_upserted} rows`;
      }).join(' · '));
      await loadConfig();
      setReload((n) => n + 1);
    } catch (e) {
      setSyncMsg(e.response?.data?.detail || 'Sync failed.');
    }
    setSyncing(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;

  const locations = config?.locations || [];
  const syncs = config?.last_syncs || {};

  return (
    <div className="space-y-6" data-testid="google-insights">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">GOOGLE</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            Business Profile, Ads, reviews and search — the numbers Google emails once a month, kept current here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {locations.length > 1 && (
            <select data-testid="google-location" value={location} onChange={(e) => setLocation(e.target.value)}
              className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:outline-none focus:border-hg-green">
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.gbp_location_id} value={l.gbp_location_id}>
                  {l.title}{l.is_australia ? ' (AU)' : ''}{l.verified ? '' : ' — unverified'}
                </option>
              ))}
            </select>
          )}
          <button data-testid="google-sync-now" onClick={syncNow} disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green disabled:opacity-40">
            {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync now
          </button>
        </div>
      </div>

      {/* Freshness per source — a stale tab must say it is stale. */}
      <div className="flex items-center gap-3 flex-wrap text-[11px] font-sans text-hg-text-dim" data-testid="google-freshness">
        <Clock size={11} />
        {['gbp', 'reviews', 'ads', 'gsc'].map((s) => {
          const last = syncs[s];
          const ok = last && last.ok;
          return (
            <span key={s} className={ok ? '' : 'text-amber-300/80'}>
              {s}: {last ? `${fmtWhen(last.started_at)}${ok ? '' : ' (failed)'}` : 'never synced'}
            </span>
          );
        })}
      </div>

      {syncMsg && <div data-testid="google-sync-msg" className="text-[11px] font-sans text-hg-text-dim border border-hg-border rounded-sm px-3 py-2">{syncMsg}</div>}

      <div className="flex gap-1.5 flex-wrap border-b border-hg-border pb-3">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`google-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
              tab === t.id ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab location={location} config={config} reload={reload} onNavigate={onNavigate} />}
      {tab === 'gbp' && <GbpTab location={location} config={config} reload={reload} />}
      {tab === 'ads' && <AdsTab config={config} reload={reload} />}
      {tab === 'seo' && <SeoTab config={config} reload={reload} />}
    </div>
  );
}
